// src/utils/formattingMarks.js
//
// View-only "Show Non-Printing Characters" decorations for the editor.
//
// These functions mutate the LIVE contentEditable DOM to draw whitespace and
// line-break glyphs, but the markers are designed so they can never corrupt the
// document:
//
//   - Whitespace wrappers ([WS_ATTR]) keep the real character inside them, and
//     the glyph is drawn via an absolutely-positioned CSS ::before, so they add
//     ZERO layout width and the live textContent is unchanged. On save/export
//     getCleanHTML() unwraps them losslessly.
//   - Line-break markers ([DECORATION_ATTR]) are empty, content-free spans; the
//     glyph is also absolutely positioned (zero width). getCleanHTML() removes
//     them entirely.
//
// Because every glyph is zero-width, pagination measurement is identical whether
// marks are on or off. Callers are still expected to CLEAR marks before any
// structural DOM operation (pagination splits text by node), and to skip the
// block that currently holds the caret (see applyFormattingMarks' activeBlock).

import { DECORATION_ATTR, WS_ATTR } from './editorSerialize';

// Whitespace characters we visualize. Defined with explicit escapes so the
// source stays pure ASCII (a literal NBSP/U+00A0 is invisible and gets mangled
// by tools that re-encode the file).
const SPACE = ' ';
const NBSP = ' ';
const TAB = '\t';

// True if the string contains any whitespace character we visualize.
function hasMarkableWhitespace(str) {
  return str.includes(SPACE) || str.includes(NBSP) || str.includes(TAB);
}

// Map a single character to its wrapper class, or null if not whitespace.
function wsClass(ch) {
  if (ch === TAB) return 'rv-ws rv-ws-tab';
  if (ch === NBSP) return 'rv-ws rv-ws-nbsp';
  if (ch === SPACE) return 'rv-ws rv-ws-space';
  return null;
}

// Structural, non-authored regions that must never be decorated.
const SKIP_SEL = '.rv-end-spacer, .rv-page-header, .rv-page-footer';
const BLOCK_SEL = 'p, li, h1, h2, h3, h4, h5, h6';

/**
 * Remove every decoration from the live DOM, restoring the original content.
 * Idempotent and lossless. Safe to call when nothing is decorated.
 * @param {Element|null} root editor host
 */
export function clearFormattingMarks(root) {
  if (!root) return;
  // Additive markers: delete outright.
  root.querySelectorAll(`[${DECORATION_ATTR}]`).forEach((n) => n.remove());
  // Whitespace wrappers: unwrap back to the literal character.
  // NOTE: do not normalize() - adjacent text nodes are left as-is so any live
  // Range/caret anchored to a sibling text node keeps its node + offset valid.
  root.querySelectorAll(`[${WS_ATTR}]`).forEach((span) => {
    span.replaceWith(document.createTextNode(span.getAttribute(WS_ATTR) ?? ''));
  });
  // Pilcrow font vars: remove so they never leak into saved/exported HTML.
  // removeProperty() alone can leave a dangling style="" attribute behind
  // (browsers don't drop the attribute just because it's empty), so drop the
  // whole attribute once nothing real is left in it.
  root.querySelectorAll(BLOCK_SEL).forEach((block) => {
    block.style.removeProperty('--rv-mark-size');
    block.style.removeProperty('--rv-mark-font');
    if (block.style.length === 0) block.removeAttribute('style');
  });
}

/**
 * (Re)build all decorations from scratch. Always clears first, so it is
 * idempotent - repeated calls never accumulate or double-mark.
 *
 * @param {Element|null} root        editor host
 * @param {Element|null} activeBlock block holding the caret; left untouched so
 *                                   typing is never disturbed (may be null)
 */
export function applyFormattingMarks(root, activeBlock) {
  if (!root) return;
  clearFormattingMarks(root);

  // 0. Pilcrow font matching - the ¶ is a CSS ::after on the block, so it
  //    defaults to the block's own font. Read the paragraph's actual text
  //    font (from its last run) and expose it as CSS vars the ::after uses.
  root.querySelectorAll(BLOCK_SEL).forEach((block) => {
    if (block.closest(SKIP_SEL)) return;
    const lastRun = lastTextNode(block);
    const source = lastRun ? lastRun.parentElement : block;
    const cs = window.getComputedStyle(source);
    block.style.setProperty('--rv-mark-size', cs.fontSize);
    block.style.setProperty('--rv-mark-font', cs.fontFamily);
  });

  // 1. Whitespace - collect target text nodes first, then mutate (never mutate
  //    the tree while the TreeWalker is walking it).
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(t) {
      if (!t.nodeValue || !hasMarkableWhitespace(t.nodeValue)) return NodeFilter.FILTER_REJECT;
      const parent = t.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (activeBlock && activeBlock.contains(t)) return NodeFilter.FILTER_REJECT;
      if (parent.closest(`${SKIP_SEL}, [${WS_ATTR}], [${DECORATION_ATTR}]`)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) textNodes.push(node);
  textNodes.forEach(wrapWhitespace);

  // 2. Line breaks - mark only <br>s inside non-empty blocks (an empty block's
  //    <br> is its own paragraph placeholder, already covered by the pilcrow).
  root.querySelectorAll('br').forEach((br) => {
    if (activeBlock && activeBlock.contains(br)) return;
    if (br.closest(SKIP_SEL)) return;
    const block = br.closest(BLOCK_SEL);
    if (!block || block.textContent.trim() === '') return;
    const mark = document.createElement('span');
    mark.setAttribute(DECORATION_ATTR, '');
    mark.setAttribute('contenteditable', 'false');
    mark.setAttribute('aria-hidden', 'true');
    mark.className = 'rv-mark rv-mark-break';
    br.parentNode.insertBefore(mark, br);
  });
}

/**
 * Replace a text node with a fragment in which every whitespace character is
 * wrapped in a zero-width marker span (real character preserved inside).
 * @param {Text} textNode
 */
function wrapWhitespace(textNode) {
  const text = textNode.nodeValue;
  const frag = document.createDocumentFragment();
  let buffer = '';
  const flush = () => {
    if (buffer) {
      frag.appendChild(document.createTextNode(buffer));
      buffer = '';
    }
  };
  for (const ch of text) {
    const cls = wsClass(ch);
    if (cls) {
      flush();
      const span = document.createElement('span');
      span.setAttribute(WS_ATTR, ch);
      span.className = `rv-mark ${cls}`;
      span.appendChild(document.createTextNode(ch));
      frag.appendChild(span);
    } else {
      buffer += ch;
    }
  }
  flush();
  textNode.replaceWith(frag);
}

/**
 * Find the last text node inside a block, in document order, without
 * descending into a nested block (e.g. a sub-list's <li>). Used to sample
 * the font the pilcrow should render in - it should match the paragraph's
 * own trailing run, not a nested block's.
 * @param {Element} block
 * @returns {Text|null}
 */
function lastTextNode(block) {
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, {
    acceptNode(t) {
      if (!t.nodeValue) return NodeFilter.FILTER_REJECT;
      const nested = t.parentElement && t.parentElement.closest(BLOCK_SEL);
      if (nested && nested !== block) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let last = null;
  let node;
  while ((node = walker.nextNode())) last = node;
  return last;
}
